import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { AdBanner } from '../../types.ts';
import { compressImageToWebP } from '../../utils/mediaOptimizer.ts';
import ConfirmModal from '../common/ConfirmModal.tsx';
import {
  Megaphone,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Upload,
  Link as LinkIcon,
  Image as ImageIcon,
  Code,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react';

interface AdsManagerProps {
  primaryColor?: string;
}

export default function AdsManager({ primaryColor = '#059669' }: AdsManagerProps) {
  const { ads, refreshData, showToast } = useApp();

  const [editingAd, setEditingAd] = useState<Partial<AdBanner> | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adToDelete, setAdToDelete] = useState<AdBanner | null>(null);

  // Initial list if ads array is empty
  const bannerList = ads || [];

  const handleCreateNew = () => {
    setEditingAd({
      id: '',
      title: '',
      position: 'sidebar',
      type: 'image',
      imageUrl: '',
      targetUrl: 'https://',
      scriptContent: '',
      enabled: true,
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const result = await compressImageToWebP(file, 1200, 0.85);
      setEditingAd((prev) => (prev ? { ...prev, imageUrl: result.dataUrl } : null));
      showToast('Gambar banner berhasil diunggah & dioptimalkan!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Gagal memproses gambar banner', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAd || !editingAd.title?.trim()) {
      showToast('Judul iklan wajib diisi', 'error');
      return;
    }

    setSaving(true);
    try {
      let updatedList: AdBanner[] = [];
      if (editingAd.id) {
        // Update existing
        updatedList = bannerList.map((a) => (a.id === editingAd.id ? ({ ...a, ...editingAd } as AdBanner) : a));
      } else {
        // Create new
        const newBanner: AdBanner = {
          id: `ad-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          title: editingAd.title.trim(),
          position: editingAd.position || 'sidebar',
          type: editingAd.type || 'image',
          imageUrl: editingAd.imageUrl || '',
          targetUrl: editingAd.targetUrl || '',
          scriptContent: editingAd.scriptContent || '',
          enabled: editingAd.enabled ?? true,
        };
        updatedList = [...bannerList, newBanner];
      }

      const res = await fetch('/api/ads', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedList),
      });

      const json = await res.json();
      if (json.success) {
        showToast(json.message || 'Banner iklan & mitra berhasil disimpan!', 'success');
        setEditingAd(null);
        await refreshData();
      } else {
        showToast(json.message || 'Gagal menyimpan banner', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Terjadi kesalahan saat menyimpan iklan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const executeDeleteAd = async () => {
    if (!adToDelete) return;
    const id = adToDelete.id;

    try {
      const updatedList = bannerList.filter((a) => a.id !== id);
      const res = await fetch('/api/ads', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedList),
      });

      const json = await res.json();
      if (json.success) {
        showToast('Banner iklan berhasil dihapus', 'success');
        setAdToDelete(null);
        await refreshData();
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menghapus banner', 'error');
      setAdToDelete(null);
    }
  };

  const handleToggleStatus = async (ad: AdBanner) => {
    try {
      const updatedList = bannerList.map((a) => (a.id === ad.id ? { ...a, enabled: !a.enabled } : a));
      const res = await fetch('/api/ads', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedList),
      });

      const json = await res.json();
      if (json.success) {
        showToast(`Status banner "${ad.title}" berhasil diubah`, 'success');
        await refreshData();
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal mengubah status banner', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-emerald-600" />
            <span>Kelola Banner Iklan & Logo Mitra Promo</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Atur tempat promosi, banner sponsor, logo mitra kerja sama, dan skrip Google Adsense di berbagai posisi website.
          </p>
        </div>

        {!editingAd && (
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 shrink-0"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus className="w-4 h-4" />
            <span>+ Tambah Banner / Logo Mitra</span>
          </button>
        )}
      </div>

      {/* Add / Edit Form Modal or Card */}
      {editingAd ? (
        <form onSubmit={handleSaveAd} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-slate-200">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-amber-500" />
              <span>{editingAd.id ? 'Edit Banner / Logo Mitra' : 'Tambah Banner / Logo Mitra Baru'}</span>
            </h3>
            <button
              type="button"
              onClick={() => setEditingAd(null)}
              className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Judul Banner / Nama Mitra *</label>
              <input
                type="text"
                required
                placeholder="Contoh: Promo Outbound Lembang 2026 / Logo Disparbud"
                value={editingAd.title || ''}
                onChange={(e) => setEditingAd({ ...editingAd, title: e.target.value })}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-800"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Posisi Penempatan Banner:</label>
              <select
                value={editingAd.position || 'sidebar'}
                onChange={(e) => setEditingAd({ ...editingAd, position: e.target.value as any })}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold text-slate-800"
              >
                <option value="top">📌 Atas Halaman (Header Top Banner)</option>
                <option value="sidebar">👉 Bilah Samping (Sidebar Blog & Detail)</option>
                <option value="in_article">📄 Sisipan Dalam Artikel (In-Article)</option>
                <option value="bottom">⬇️ Bawah Beranda / Footer (Mitra Sponsor Grid)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Tipe Banner:</label>
              <select
                value={editingAd.type || 'image'}
                onChange={(e) => setEditingAd({ ...editingAd, type: e.target.value as any })}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold text-slate-800"
              >
                <option value="image">🖼️ Gambar / Banner Grafis (Upload / URL)</option>
                <option value="script">💻 Skrip Kode HTML / Custom Script (AdSense / iFrame)</option>
              </select>
            </div>

            <div className="flex items-center pt-5">
              <label className="inline-flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-2xs">
                <input
                  type="checkbox"
                  checked={Boolean(editingAd.enabled)}
                  onChange={(e) => setEditingAd({ ...editingAd, enabled: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 rounded-md border-slate-300 focus:ring-emerald-500"
                />
                <span className="text-xs font-bold text-slate-800">
                  Aktifkan & Tampilkan di Website
                </span>
              </label>
            </div>
          </div>

          {editingAd.type === 'image' ? (
            <div className="space-y-3 p-4 bg-white rounded-xl border border-slate-200">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Link Target / URL saat Diklik:</label>
                <input
                  type="url"
                  placeholder="https://wa.me/6281234567890 atau https://domain.com/promo"
                  value={editingAd.targetUrl || ''}
                  onChange={(e) => setEditingAd({ ...editingAd, targetUrl: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Upload Gambar / Banner (WebP Kompresi):</label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="https://images.unsplash.com/... atau URL gambar"
                    value={editingAd.imageUrl || ''}
                    onChange={(e) => setEditingAd({ ...editingAd, imageUrl: e.target.value })}
                    className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono"
                  />
                  <label className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold cursor-pointer flex items-center gap-1.5 shrink-0">
                    <Upload className="w-3.5 h-3.5" />
                    <span>{isUploading ? 'Memproses...' : 'Upload Gambar'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {editingAd.imageUrl && (
                <div className="mt-2 p-2 bg-slate-100 rounded-xl border border-slate-200 max-w-md">
                  <p className="text-[10px] font-bold text-slate-500 mb-1">Preview Banner:</p>
                  <img src={editingAd.imageUrl} alt="Preview" className="max-h-36 rounded-lg object-contain bg-slate-900 mx-auto" />
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <label className="text-xs font-bold text-slate-700 block mb-1">Kode Skrip AdSense / HTML Script:</label>
              <textarea
                rows={4}
                placeholder="<script async src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'></script>..."
                value={editingAd.scriptContent || ''}
                onChange={(e) => setEditingAd({ ...editingAd, scriptContent: e.target.value })}
                className="w-full px-3 py-2 text-xs bg-slate-900 text-emerald-400 font-mono border border-slate-700 rounded-xl"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setEditingAd(null)}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 rounded-xl text-white font-bold text-xs shadow-md disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {saving ? 'Menyimpan...' : 'Simpan Banner'}
            </button>
          </div>
        </form>
      ) : null}

      {/* List of Ads / Mitra */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {bannerList.length === 0 ? (
          <div className="col-span-full p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300">
            <Megaphone className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <h4 className="font-bold text-sm text-slate-700">Belum Ada Banner Iklan / Logo Mitra</h4>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Klik tombol "+ Tambah Banner / Logo Mitra" untuk memasang spanduk promo, logo partner instansi, atau iklan Google Adsense.
            </p>
          </div>
        ) : (
          bannerList.map((ad) => (
            <div
              key={ad.id}
              className={`p-4 rounded-2xl border bg-white shadow-2xs flex flex-col justify-between transition-all ${
                ad.enabled ? 'border-slate-200' : 'border-slate-200 bg-slate-50/70 opacity-75'
              }`}
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      ad.position === 'top'
                        ? 'bg-purple-100 text-purple-800'
                        : ad.position === 'sidebar'
                        ? 'bg-blue-100 text-blue-800'
                        : ad.position === 'in_article'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {ad.position === 'top'
                      ? 'Header Top'
                      : ad.position === 'sidebar'
                      ? 'Sidebar'
                      : ad.position === 'in_article'
                      ? 'In-Article'
                      : 'Mitra Footer'}
                  </span>

                  <button
                    onClick={() => handleToggleStatus(ad)}
                    className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                      ad.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {ad.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    <span>{ad.enabled ? 'Aktif' : 'Nonaktif'}</span>
                  </button>
                </div>

                {ad.type === 'image' && ad.imageUrl ? (
                  <div className="aspect-16/7 rounded-xl overflow-hidden bg-slate-900 mb-3 relative group">
                    <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="p-3 bg-slate-900 rounded-xl mb-3 font-mono text-[11px] text-emerald-400 overflow-hidden line-clamp-3">
                    {ad.scriptContent || '<Script Kode HTML>'}
                  </div>
                )}

                <h4 className="font-bold text-sm text-slate-900">{ad.title}</h4>
                {ad.targetUrl && (
                  <a
                    href={ad.targetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1 mt-1 truncate font-mono"
                  >
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    <span className="truncate">{ad.targetUrl}</span>
                  </a>
                )}
              </div>

              <div className="flex items-center justify-end gap-1.5 pt-3 mt-3 border-t border-slate-100">
                <button
                  onClick={() => setEditingAd(ad)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Edit Banner"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setAdToDelete(ad)}
                  className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer"
                  title="Hapus Banner"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(adToDelete)}
        title="Hapus Banner Iklan"
        message={`Apakah Anda yakin ingin menghapus banner iklan "${adToDelete?.title || 'ini'}"?`}
        confirmLabel="Ya, Hapus Banner"
        isDanger={true}
        onConfirm={executeDeleteAd}
        onCancel={() => setAdToDelete(null)}
      />
    </div>
  );
}
